/**
 * CALL-CENTER-LEAD-360-SALESFORCE-EXPERIENCE-V1 -- pure view models for the
 * enterprise Lead record experience (Refs #315; findings 4/5/6/8).
 *
 * ONE home for the presentation derivations the rebuilt Lead record needs:
 *   - the OWNERSHIP-PLANE display rule (finding 4: the stale
 *     "Next action owner: COC" after a personal assignment) -- the header
 *     renders the LIVE open-work-item owner when one exists, renders the
 *     rule-ladder ROLE guess ONLY as the correctly-labeled "Responsible
 *     team" plane and ONLY while the lead is NOT personally assigned, and
 *     renders NOTHING from the ladder once a named owner exists (the Owner
 *     row is the accountable plane; a rule guess never contradicts it).
 *     No synchronization is faked: the rule ladder itself is untouched.
 *   - the EVIDENCE CLASSIFICATION of activity entries (agent-recorded /
 *     integration-verified / system-derived) -- manual operator entries are
 *     never presented as provider-verified.
 *   - the UPCOMING & OVERDUE derivation for the activity rail (callback /
 *     current appointment / follow-up), overdue first.
 *   - the ACTIVITY RAIL entries -- EVERY event renders (a stored event
 *     without a specialized renderer gets the generic humanized entry);
 *     recorded activity is never silently dropped.
 *   - the LEAD HISTORY rows -- the read-only history table from the SAME
 *     event wire; the ONLY field-level before/after stored today is the
 *     reschedule old->new time pair (gap registered:
 *     gap.field-level-before-after-history-coverage).
 *   - the DETAILS field:value form model -- compact labeled facts, ALL
 *     read-only (no proven per-field writer exists for any Details field;
 *     every mutation rides the action bar's EXISTING controls).
 *   - the ACTION BAR model -- in-place anchors to already-existing,
 *     role-held controls only (LES-016: role-shaped or absent). No new
 *     writer, no navigation.
 *
 * PURE + DETERMINISTIC: no React, no fetch, no Date.now() (callers inject
 * nowMs). PHI posture unchanged: everything here reads the role-projected
 * Lead 360 wire; no digits, no clinical fields, no new data.
 */

import type {
  Lead360Event,
  Lead360Input,
  Lead360View,
} from '@/dashboard/owner/lead-360-view';
import {
  compactSourceSummary,
  eventTypeLabel,
  formatTimestampIst,
  outcomeReasonLabel,
  sourceChannelLabel,
  sourceMediumLabel,
} from '@/dashboard/owner/Lead360Panel';
import { isLead360SectionVisible } from '@/dashboard/owner/lead-360-view';
import type { LeadRecordCurrentTruth } from '@/dashboard/record/current-truth';
import type { LastContactAttempt } from '@/dashboard/leads/last-contact-attempt';
import { humanizeDispositionSlug } from '@/dashboard/leads/last-contact-attempt';
// THE ONE age formatter (the leakage/queue vocabulary) -- reused, never
// re-implemented, so "2h 15m" reads identically on the queue and the record.
import { formatLeakageAge } from '@/dashboard/sla-leakage/sla-leakage-view';
// THE ONE escalation copy home (the ownership controls own the vocabulary).
import { OWNER_ESCALATED_HINT, OWNER_POOL_VALUE } from '@/dashboard/leads/LeadOwnerControls';
// THE ONE digit-free phone-availability copy home (CONTACT-REVEAL-AUDIT-01).
import { PHONE_AVAILABILITY_COPY } from '@/dashboard/owner/LeadContactReveal';
// THE ONE role-label humanizer (coc -> COC, call_center_agent -> Call center) --
// reused for the timeline author column (LEAD360-G6-AUTHOR-COLUMN-FIX), never
// re-implemented.
import { roleLabel } from '@/dashboard/ui/role-presentation';

// ---------------------------------------------------------------------
// Mission 4 -- the ownership-plane display rule
// ---------------------------------------------------------------------

export const NEXT_ACTION_OWNER_ROW_LABEL = 'Next action owner';
export const RESPONSIBLE_TEAM_ROW_LABEL = 'Responsible team';

/**
 * LEAD-RECORD-POLISH-BATCH-V1 (CC-08 -- THE TWO OWNERS DISAMBIGUATE; founder
 * walk 2026-08-03). The record showed "Owner" beside "Next action owner" and a
 * worker could not tell which one meant a PERSON. The fix is vocabulary, not
 * data: the ROUTING plane is a TEAM and the PERSON plane is who CLAIMED it.
 *   - TEAM_ROW_LABEL       the routing target (a team, never a person)
 *   - CLAIMED_BY_ROW_LABEL the accountable PERSON (LeadOwnershipView)
 * The long-form plane labels above are UNCHANGED: they stay the honesty
 * footer's vocabulary (DataHonesty), where the plane distinction is the point.
 */
export const TEAM_ROW_LABEL = 'Team';
export const CLAIMED_BY_ROW_LABEL = 'Claimed by';

/** The finding-4 plane distinction, demoted from the LABEL to a micro-note so
 *  the two owner rows can carry the plain CC-08 labels without losing it. */
export const TEAM_NOTE_OPEN_ACTION = 'Owns the open action';
export const TEAM_NOTE_ROUTING_DEFAULT = 'Routing target';

export interface OwnershipPlaneRow {
  /** 'Next action owner' (a LIVE open work item's owner) or
   *  'Responsible team' (the rule-derived routing-role default). */
  readonly label: string;
  /** CC-08: the record header's plain label for BOTH planes ('Team'). */
  readonly shortLabel: string;
  readonly value: string;
  /** CC-08: which plane, as a micro-note instead of a competing label. */
  readonly note: string;
  /** Which plane produced the row (pinned in tests). */
  readonly plane: 'open_action' | 'responsible_team';
}

/**
 * LEAD-RECORD-POLISH-BATCH-V1 (CC-12 -- ROLE TOKENS NEVER RENDER RAW; founder
 * walk 2026-08-03). THE record's role DISPLAY-LABEL layer: a stored routing
 * role token is a STORAGE value and must never reach the screen as
 * `call_center_agent`. Display only -- storage, authorization, and the wire
 * vocabulary are untouched (the token still travels; only the render changes).
 *
 * The routing plane names a TEAM, so the labels read as teams (the founder's
 * own example: `call_center_agent` -> "Call center team"). An UNKNOWN or
 * future token de-cases into a readable label rather than echoing raw --
 * the same defensive posture as the shell's ONE roleLabel owner
 * (ui/role-presentation.ts), which stays the owner of the SIGNED-IN role's
 * label and is deliberately not reused here (a person's role chip and a
 * routing team are different vocabularies).
 */
const TEAM_DISPLAY_LABEL: Readonly<Record<string, string>> = {
  call_center_agent: 'Call center team',
  receptionist: 'Receptionist',
  coc: 'COC',
  regional_head: 'Regional Head',
  gm_agm: 'GM/AGM',
  owner: 'Owner',
};

/** THE display label for a routing role token. Never returns a raw token. */
export function leadTeamDisplayLabel(token: string | null | undefined): string | null {
  if (typeof token !== 'string' || token.trim() === '') return null;
  const key = token.trim();
  const mapped = TEAM_DISPLAY_LABEL[key];
  if (mapped !== undefined) return mapped;
  // Unknown/future token: de-case, never echo raw (CC-12).
  return key
    .split('_')
    .filter((p) => p.length > 0)
    .map((p, i) => (i === 0 ? p.charAt(0).toUpperCase() + p.slice(1) : p))
    .join(' ');
}

/**
 * THE finding-4 rule. The old header rendered `view.assignedRoleLabel`
 * under "Next action owner" unconditionally -- for a lead with no open
 * work item that value is the R1-R6 rule-ladder ROLE guess (e.g. "COC",
 * lib/adapters/supabase/lead-360-read-model.ts deriveLeadOwnership), which
 * is a ROUTING-plane default, not the operational owner -- so it kept
 * reading "COC" after a manager personally assigned the lead (founder live
 * finding 4, 2026-07-22).
 *
 * Resolution (render truth, never fake sync):
 *   - a LIVE open lead_actions row exists (record.openActionOwner) -> its
 *     owner IS the next-action owner: render it under the accurate label.
 *   - no open action + the lead IS personally assigned -> render NOTHING
 *     from the ladder (the Owner row carries the accountable plane; a rule
 *     guess must not contradict it).
 *   - no open action + NOT personally assigned -> the ladder value is the
 *     honest routing default: render it, correctly labeled as the
 *     "Responsible team" plane (never as an owner).
 */
export function resolveOwnershipPlaneRow(
  record: Lead360Input,
  view: Lead360View,
): OwnershipPlaneRow | null {
  if (record.openActionOwner !== undefined) {
    return {
      label: NEXT_ACTION_OWNER_ROW_LABEL,
      shortLabel: TEAM_ROW_LABEL,
      // CC-12: resolve the TEAM label from the stored TOKEN through the
      // display layer; the view's own label is the fallback for a wire that
      // carried no token (never a raw token either way).
      value:
        leadTeamDisplayLabel(record.openActionOwner.assignedRole) ??
        view.assignedRoleLabel,
      note: TEAM_NOTE_OPEN_ACTION,
      plane: 'open_action',
    };
  }
  if (record.ownerAssignment?.assigned === true) return null;
  return {
    label: RESPONSIBLE_TEAM_ROW_LABEL,
    shortLabel: TEAM_ROW_LABEL,
    value: leadTeamDisplayLabel(record.assignedRole) ?? view.assignedRoleLabel,
    note: TEAM_NOTE_ROUTING_DEFAULT,
    plane: 'responsible_team',
  };
}

// ---------------------------------------------------------------------
// Mission 8 -- evidence classification (merged-core posture preserved)
// ---------------------------------------------------------------------

export type LeadEvidenceClass =
  | 'agent_recorded'
  | 'integration_verified'
  | 'system_derived';

export const EVIDENCE_CLASS_LABEL: Readonly<Record<LeadEvidenceClass, string>> = {
  agent_recorded: 'Agent-recorded',
  integration_verified: 'Integration-verified',
  system_derived: 'System-derived',
};

/** Operator-logged lead_events tokens (manual entries -- an operator typed
 *  or clicked them; never provider-verified). */
const AGENT_RECORDED_EVENT_TYPES: ReadonlySet<string> = new Set([
  'called',
  'not_answered',
  'missed_call',
  'follow_up_needed',
  'closed',
  'visited',
]);

/**
 * Classify ONE activity entry's evidence. The law (merged core, preserved):
 * manually entered phone/WhatsApp outcomes are NEVER presented as
 * provider-verified.
 *   - a disposition row (typed tokens) or a manual operator lead_events
 *     token -> agent-recorded;
 *   - 'call_logged' (a linked Office24x7 telephony record) ->
 *     integration-verified;
 *   - everything else (system projections from appointment_events /
 *     walkins / audit_log / the capture writer, and any unknown future
 *     token) -> system-derived (the honest default -- a foreign token is
 *     never guessed into a stronger class).
 */
export function eventEvidenceClass(event: Lead360Event): LeadEvidenceClass {
  if (
    typeof event.dispositionOutcomeId === 'string' &&
    event.dispositionOutcomeId.length > 0
  ) {
    return 'agent_recorded';
  }
  if (event.eventType === 'call_logged') return 'integration_verified';
  if (AGENT_RECORDED_EVENT_TYPES.has(event.eventType)) return 'agent_recorded';
  return 'system_derived';
}

// ---------------------------------------------------------------------
// Mission 8 -- activity rail entries (every event renders)
// ---------------------------------------------------------------------

export interface LeadActivityEntry {
  readonly key: string;
  readonly eventType: string;
  /** Humanized consequence label (type label + controlled reason). */
  readonly label: string;
  /** The PHI-safe actor/summary line from the read model, or null. */
  readonly actorLine: string | null;
  readonly whenLabel: string;
  /**
   * ENTERPRISE-CRM-EXPERIENCE-ELEVATION-V1 (G3, presentation grouping
   * only): the day/time split of the SAME formatTimestampIst output
   * ("22 Jul 2026, 3:00 PM IST" -> "22 Jul 2026" + "3:00 PM IST") so the
   * rail can group entries under day headers. No new date math, no new
   * truth -- a pure split of the one zone-labeled stamp.
   */
  readonly dayLabel: string;
  readonly timeLabel: string;
  readonly evidence: LeadEvidenceClass;
  readonly evidenceLabel: string;
  /** Typed disposition facts when the entry is a disposition row. */
  readonly callbackDueLabel: string | null;
}

/** Split one formatTimestampIst stamp into its day + time parts (pure).
 *  A stamp without the ", " seam (unparseable input echoed verbatim)
 *  returns itself as the day with an empty time -- never invented. */
export function splitIstStamp(whenLabel: string): {
  readonly dayLabel: string;
  readonly timeLabel: string;
} {
  const seam = whenLabel.indexOf(', ');
  if (seam === -1) return { dayLabel: whenLabel, timeLabel: '' };
  return {
    dayLabel: whenLabel.slice(0, seam),
    timeLabel: whenLabel.slice(seam + 2),
  };
}

/**
 * Project EVERY wire event (newest-first, the read model's order) into a
 * rail entry. Unknown event types render through the generic humanized
 * label (`eventTypeLabel` de-cases them) with actor + timestamp --
 * recorded activity is never silently dropped.
 */
export function buildActivityRailEntries(
  events: readonly Lead360Event[] | undefined,
): readonly LeadActivityEntry[] {
  return (events ?? []).map((e, i) => {
    const reason =
      e.eventType === 'lead_not_converted' &&
      typeof e.outcomeReason === 'string' &&
      e.outcomeReason.trim() !== ''
        ? ` (${outcomeReasonLabel(e.outcomeReason)})`
        : '';
    const cls = eventEvidenceClass(e);
    const whenLabel = formatTimestampIst(e.occurredAt);
    const { dayLabel, timeLabel } = splitIstStamp(whenLabel);
    return {
      key: `${e.eventType}:${e.occurredAt}:${i}`,
      eventType: e.eventType,
      label: `${eventTypeLabel(e.eventType)}${reason}`,
      actorLine:
        typeof e.summary === 'string' && e.summary.trim() !== ''
          ? e.summary
          : null,
      whenLabel,
      dayLabel,
      timeLabel,
      evidence: cls,
      evidenceLabel: EVIDENCE_CLASS_LABEL[cls],
      callbackDueLabel:
        typeof e.dispositionCallbackAtIso === 'string' &&
        e.dispositionCallbackAtIso.trim() !== ''
          ? formatTimestampIst(e.dispositionCallbackAtIso)
          : null,
    };
  });
}

// ---------------------------------------------------------------------
// Mission 8 -- upcoming & overdue (rail head)
// ---------------------------------------------------------------------

export interface UpcomingItem {
  readonly key: 'callback' | 'appointment' | 'follow_up';
  readonly label: string;
  readonly dueLabel: string | null;
  readonly overdue: boolean;
}

export const UPCOMING_EMPTY_COPY = 'Nothing scheduled or overdue.';

/**
 * The rail-head work list from the SAME reads the record already holds:
 * the last recorded callback promise, the current appointment, and the
 * derived follow-up state. Overdue items first (the oldest due leads),
 * then upcoming by due time, then undated items. Deterministic; `nowMs`
 * injected.
 */
export function buildUpcomingItems(
  truth: LeadRecordCurrentTruth,
  lastAttempt: LastContactAttempt | null,
  nowMs: number,
): readonly UpcomingItem[] {
  interface Staged extends UpcomingItem {
    readonly dueMs: number | null;
  }
  const staged: Staged[] = [];

  if (lastAttempt !== null && lastAttempt.callbackAtIso !== null) {
    const ms = Date.parse(lastAttempt.callbackAtIso);
    const due = Number.isFinite(ms) ? ms : null;
    staged.push({
      key: 'callback',
      label: 'Callback promised',
      dueLabel: formatTimestampIst(lastAttempt.callbackAtIso),
      overdue: due !== null && due < nowMs,
      dueMs: due,
    });
  }

  const appt = truth.currentAppointment;
  if (appt.kind === 'active_future' || appt.kind === 'active_past') {
    const ms = appt.appointmentTime !== null ? Date.parse(appt.appointmentTime) : NaN;
    staged.push({
      key: 'appointment',
      label:
        appt.kind === 'active_future'
          ? 'Appointment scheduled'
          : 'Appointment date passed',
      dueLabel:
        appt.appointmentTime !== null
          ? formatTimestampIst(appt.appointmentTime)
          : null,
      overdue: appt.kind === 'active_past',
      dueMs: Number.isFinite(ms) ? ms : null,
    });
  }

  if (truth.currentFollowUpState.kind === 'required') {
    staged.push({
      key: 'follow_up',
      label: truth.currentFollowUpState.whyLabel ?? 'Follow-up required',
      dueLabel: null,
      overdue: false,
      dueMs: null,
    });
  }

  staged.sort((a, b) => {
    if (a.overdue !== b.overdue) return a.overdue ? -1 : 1;
    if (a.dueMs !== null && b.dueMs !== null && a.dueMs !== b.dueMs) {
      return a.dueMs - b.dueMs;
    }
    if ((a.dueMs === null) !== (b.dueMs === null)) return a.dueMs === null ? 1 : -1;
    return 0;
  });
  return staged.map(({ dueMs: _dueMs, ...item }) => item);
}

// ---------------------------------------------------------------------
// Mission 3 -- the Details field:value form model (ALL read-only)
// ---------------------------------------------------------------------

export interface LeadDetailFieldDef {
  readonly label: string;
  /** Resolved display value, or null -> the field's honest missing copy. */
  readonly value: string | null;
  readonly missingLabel?: string;
  readonly testId: string;
}

/**
 * The compact Details form fields, role-gated by the SAME section manifest
 * the server projected (attribution fields render only for tiers holding
 * 'source'). EVERY field is READ-ONLY: no proven per-field writer exists
 * for any of these exact fields, so per the FIELD EDITABILITY RULE no edit
 * affordance renders anywhere in this form -- mutations ride the action
 * bar's existing controls (reveal / disposition / workbench / ownership).
 *
 * W1 REPETITION KILL (LEAD-RECORD-AEDS-V1 founder walk 2026-07-24 -- binding):
 * every fact renders EXACTLY ONCE in its canonical home; a second full
 * field:value render is the defect the founder named ("one stage value shown
 * three times"). So the Details form carries ONLY the reference facts with NO
 * other on-screen home and DROPS the ones the header / band / stage path bar
 * already own:
 *   - identity (Lead name = the header H1; Clinic, Owner, Created) -> the
 *     RecordHeader identity strip is the one home;
 *   - stage (CRM stage) -> the LeadStagePathBar (canonical ladder) + the
 *     band's single labeled 'CRM stage' chip (the one lawful compact chip);
 *   - status (Appointment, Patient record, SLA, Next action) -> the
 *     CurrentStateBand is the one authoritative status home.
 * What remains is the genuine DETAIL: the contact-discipline timing (last
 * attempt / result / callback due -- no band/header home) and the full
 * marketing attribution breakdown (the header shows only a compact source
 * chip). This never widens a read: attribution stays source-tier gated.
 */
export function buildLeadDetailFields(input: {
  readonly record: Lead360Input;
  readonly view: Lead360View;
  readonly truth: LeadRecordCurrentTruth;
  readonly lastAttempt: LastContactAttempt | null;
}): readonly LeadDetailFieldDef[] {
  const { record, view, lastAttempt } = input;
  const showSource = isLead360SectionVisible(view.visibleSections, 'source');

  const fields: LeadDetailFieldDef[] = [
    {
      label: 'Last contact',
      value:
        lastAttempt !== null ? formatTimestampIst(lastAttempt.atIso) : null,
      missingLabel: 'No contact attempt recorded yet',
      testId: 'lead-details-last-contact',
    },
  ];

  if (lastAttempt !== null) {
    fields.push({
      label: 'Last contact result',
      value: humanizeDispositionSlug(lastAttempt.resultId),
      testId: 'lead-details-last-result',
    });
  }

  fields.push({
    label: 'Callback due',
    value:
      lastAttempt !== null && lastAttempt.callbackAtIso !== null
        ? formatTimestampIst(lastAttempt.callbackAtIso)
        : null,
    missingLabel: 'None scheduled',
    testId: 'lead-details-callback',
  });

  if (showSource) {
    const channel = sourceChannelLabel(record.sourceNormalized, record.utmSource);
    fields.push({
      label: 'Channel',
      value: channel !== '' ? channel : null,
      missingLabel: 'Source not captured',
      testId: 'lead-details-channel',
    });
    const medium = sourceMediumLabel(record.utmMedium);
    if (medium !== '') {
      fields.push({
        label: 'Medium',
        value: medium,
        testId: 'lead-details-medium',
      });
    }
    if (
      typeof record.utmCampaign === 'string' &&
      record.utmCampaign.trim() !== ''
    ) {
      fields.push({
        label: 'Campaign',
        value: record.utmCampaign.trim(),
        testId: 'lead-details-campaign',
      });
    }
    fields.push({
      label: 'Paid click',
      value: record.paidClickPresent === true ? 'Yes' : 'No',
      testId: 'lead-details-paid-click',
    });
  }

  return fields;
}

// ---------------------------------------------------------------------
// LEAD-360-CONFORMANCE-B2 -- the composed rail's "Lead details" section
// (F2 region 7 amended map / F1 TIER 3 "LEAD DETAILS SECTION"): the
// record's six basic facts, read-only, counted exactly 6 on every frame.
// A DIFFERENT field set from buildLeadDetailFields above (contact-timing +
// attribution -- the Details zone's own reference facts): this is the
// identity/ownership/lifecycle set the pack draws in the rail, reusing
// values the page already resolves (Owner/Stage/Captured duplicate the
// Owner rail section and the stage path bar BY FOUNDER ORDER -- the
// SAY-ONCE RIDER, F2 entry condition 5(i); Ruling 1/2 in the B2 run
// record). No email row: no lead email field exists on base (F1).
// ---------------------------------------------------------------------

export function buildLeadDetailsRailFields(input: {
  readonly view: Lead360View;
  readonly truth: LeadRecordCurrentTruth;
}): readonly LeadDetailFieldDef[] {
  const { view, truth } = input;
  const owner = truth.currentOwner;
  const ownerValue =
    owner.kind === 'assigned'
      ? `${owner.label ?? 'Assigned'}${owner.isMine ? ' (you)' : ''}`
      : owner.kind === 'pool'
        ? OWNER_POOL_VALUE
        : null;
  return [
    {
      label: 'Name',
      value: view.leadName ?? `Lead #${view.safeLeadId}`,
      testId: 'lead-rail-details-name',
    },
    {
      label: 'Phone',
      value: PHONE_AVAILABILITY_COPY[view.phoneAvailability],
      testId: 'lead-rail-details-phone',
    },
    {
      label: 'Clinic',
      value: view.clinicLabel,
      testId: 'lead-rail-details-clinic',
    },
    {
      label: 'Owner',
      value: ownerValue,
      missingLabel: 'Not tracked on this record',
      testId: 'lead-rail-details-owner',
    },
    {
      label: 'Stage',
      value: truth.currentLeadStage.label,
      testId: 'lead-rail-details-stage',
    },
    {
      label: 'Captured',
      value: formatTimestampIst(view.createdAtDisplay),
      testId: 'lead-rail-details-captured',
    },
  ];
}

// ---------------------------------------------------------------------
// ENTERPRISE-CRM-EXPERIENCE-ELEVATION-V1 (G4) -- details sub-grouping.
// Pure PRESENTATION grouping of the SAME field defs (order inside each
// group preserved; no field added, removed, or re-derived): the W1-deduped
// reference facts scan as labeled groups (Lifecycle / Attribution -- the
// Contact group's identity facts moved to their one home in the header, so
// it is empty and absent). An unmapped future label lands in Lifecycle --
// shown, never dropped.
// ---------------------------------------------------------------------

export type LeadDetailGroupKey = 'contact' | 'lifecycle' | 'attribution';

export const LEAD_DETAIL_GROUP_TITLE: Readonly<
  Record<LeadDetailGroupKey, string>
> = {
  contact: 'Contact',
  lifecycle: 'Lifecycle',
  attribution: 'Attribution',
};

const DETAIL_GROUP_BY_LABEL: Readonly<Record<string, LeadDetailGroupKey>> = {
  // W1: the header / band / stage path bar own identity, status, and stage;
  // the Details form keeps only the contact-discipline timing + the full
  // attribution breakdown (the two facts with no other on-screen home).
  'Last contact': 'lifecycle',
  'Last contact result': 'lifecycle',
  'Callback due': 'lifecycle',
  Channel: 'attribution',
  Medium: 'attribution',
  Campaign: 'attribution',
  'Paid click': 'attribution',
};

export interface LeadDetailFieldGroup {
  readonly key: LeadDetailGroupKey;
  readonly title: string;
  readonly fields: readonly LeadDetailFieldDef[];
}

/** Group the flat detail fields (groups render in the fixed
 *  Contact -> Lifecycle -> Attribution order; empty groups are absent). */
export function groupLeadDetailFields(
  fields: readonly LeadDetailFieldDef[],
): readonly LeadDetailFieldGroup[] {
  const buckets: Record<LeadDetailGroupKey, LeadDetailFieldDef[]> = {
    contact: [],
    lifecycle: [],
    attribution: [],
  };
  for (const field of fields) {
    buckets[DETAIL_GROUP_BY_LABEL[field.label] ?? 'lifecycle'].push(field);
  }
  return (['contact', 'lifecycle', 'attribution'] as const)
    .filter((key) => buckets[key].length > 0)
    .map((key) => ({
      key,
      title: LEAD_DETAIL_GROUP_TITLE[key],
      fields: buckets[key],
    }));
}

// ---------------------------------------------------------------------
// Mission 2 -- the action bar model (existing role-held doors only)
// ---------------------------------------------------------------------

export interface LeadActionDef {
  readonly key: string;
  readonly label: string;
  /** In-page anchor target (the existing control's section id). */
  readonly targetId: string;
}

/** Anchor targets (section ids stamped by the record layout). */
export const ACTION_TARGET_CONTACT = 'lead-record-contact';
export const ACTION_TARGET_WORK = 'lead-record-work';
export const ACTION_TARGET_APPOINTMENTS = 'record360-zone-appointments';
export const ACTION_TARGET_OWNERSHIP = 'lead-record-ownership';

/**
 * RA-2 CHAIN-AS-SEAM (LEAD-TRUTH-AND-DEPENDENT-STATE-CORRECTIVE-V1;
 * re-doored by LEAD-CONTACT-FLOW-V1): after a disposition save whose next
 * action is book_appointment, the record chains into the EXISTING booking
 * flow -- which now lives in the record's BOOKING MODAL (the one booking
 * door), no longer a rail anchor. The save is COMPLETE before this is
 * consulted -- invocation only, never a merged submit, zero new booking
 * code. Every other next action stays inline.
 */
export function dispositionChainsToBooking(
  nextActionId: string | null,
): boolean {
  return nextActionId === 'book_appointment';
}

/**
 * The role-shaped action set. Every entry reaches an already-rendered,
 * already-authorized control (the NEXT-card contact workflow, the ONE
 * disposition panel, the existing appointment workbench, the existing
 * ownership controls) -- no new writer, no navigation, and an action whose
 * control is not rendered for this caller is ABSENT, never a dead door
 * (LES-016).
 *
 * LEAD-CONTACT-FLOW-V1 (WALK findings W-27/W-28, founder ruling
 * 2026-08-08): the 'reveal', 'log_outcome' and 'schedule_callback'
 * entries are DELETED -- the contact act (reveal + audit + advance to
 * disposition, callback included as a disposition next action) now lives
 * INSIDE the NEXT card as the ONE 'contact_lead' workflow; two
 * near-identical primary verbs beside NEXT were the W-28 defect. The
 * booking entry SPLITS on the canonical bookability rule (the existing
 * workbench derivation -- an active appointment shows Manage, never
 * Book): 'book_appointment' opens the record's booking MODAL (the one
 * booking door, W-29); 'manage_appointment' anchors to the rail's
 * Appointments section where the existing manage acts live.
 */
export function buildLeadActionBar(input: {
  readonly hasContactWorkflow: boolean;
  readonly canBookAppointment: boolean;
  readonly hasActiveAppointment: boolean;
  readonly hasOwnershipControls: boolean;
  readonly canReassign: boolean;
}): readonly LeadActionDef[] {
  const actions: LeadActionDef[] = [];
  if (input.hasContactWorkflow) {
    actions.push({
      key: 'contact_lead',
      label: 'Contact this lead',
      targetId: ACTION_TARGET_WORK,
    });
  }
  if (input.canBookAppointment) {
    actions.push({
      key: 'book_appointment',
      label: 'Book appointment',
      targetId: ACTION_TARGET_APPOINTMENTS,
    });
  }
  if (input.hasActiveAppointment) {
    actions.push({
      key: 'manage_appointment',
      label: 'Manage appointment',
      targetId: ACTION_TARGET_APPOINTMENTS,
    });
  }
  if (input.hasOwnershipControls && input.canReassign) {
    actions.push({
      key: 'reassign',
      label: 'Reassign',
      targetId: ACTION_TARGET_OWNERSHIP,
    });
  }
  return actions;
}

// ---------------------------------------------------------------------
// LEAD-RECORD-POLISH-BATCH-V1 (CC-03-lite) -- THE ONE EMPHASIZED ACTION
// ---------------------------------------------------------------------

/**
 * Which ONE of the caller's already-rendered actions carries the emphasis
 * (UX LAW 4 / DENSITY rule 8: one primary action per surface). A SPOTLIGHT,
 * not a redesign: the set, the order, the labels, the anchors and the role
 * shaping are all unchanged -- only WHICH entry is drawn as the primary.
 *
 * DERIVED FROM LIFECYCLE STATE, never hard-coded: the record's own next
 * lawful action (the current-truth contract) names the plane that owns the
 * next step, and the emphasis follows it --
 *   appointment / visit / payment plane -> the APPOINTMENTS zone owns it
 *     (book / confirm / reschedule all live in that one workbench);
 *   conversion / stage / unknown        -> the CALL owns it (log the outcome).
 * An emphasis whose control this caller does NOT hold degrades to the FIRST
 * lawful action (LES-016: never a dead door, never emphasis on an absent one),
 * and an empty set emphasizes nothing.
 */
export function resolveEmphasisActionKey(input: {
  readonly actions: readonly LeadActionDef[];
  readonly nextActionSource: string;
}): string | null {
  const { actions, nextActionSource } = input;
  if (actions.length === 0) return null;
  // LEAD-CONTACT-FLOW-V1: the appointment/visit/payment plane prefers the
  // booking door when the canonical rule permits booking, else the rail's
  // existing manage acts; every other plane's next step is the CONTACT
  // workflow (which carries the disposition advance -- callback included).
  const preferredLadder =
    nextActionSource === 'appointment' ||
    nextActionSource === 'visit' ||
    nextActionSource === 'payment'
      ? ['book_appointment', 'manage_appointment']
      : ['contact_lead'];
  for (const preferred of preferredLadder) {
    if (actions.some((a) => a.key === preferred)) return preferred;
  }
  return actions[0]!.key;
}

// ---------------------------------------------------------------------
// LEAD-RECORD-POLISH-BATCH-V1 (CC-11) -- THE UNCLAIMED STATE EXPLAINS ITSELF
// ---------------------------------------------------------------------

/** The org's configured lead-assignment mode (DashboardMe.leadAssignmentMode,
 *  org_workflow_settings `lead_assignment.mode` -- server truth). */
export type LeadAssignmentModeToken =
  | 'shared_pool'
  | 'manager_assign'
  | 'round_robin';

/**
 * WHY is this lead still unclaimed? The founder's walk found the unclaimed
 * seat saying only "Not yet claimed" -- true, but it left the worker with no
 * idea whether to wait, claim, or escalate.
 *
 * EVERY sentence here is derived from REAL routing state -- the org's
 * CONFIGURED mode as the server resolved it onto /me, plus the record's own
 * ownership state -- and NOTHING is invented:
 *   - round_robin  : the auto-assign engine runs AT CAPTURE over the eligible,
 *     available rotation (lib/lead-routing-hook.ts). A lead that is STILL in
 *     the pool is one the rotation did not place, so naming the empty rotation
 *     is a statement about state, not a guess.
 *   - manager_assign : self-claim is disabled by mode -- a manager assigns.
 *   - shared_pool  : anyone on the team may claim it.
 *   - mode ABSENT (older /me, a role outside the assignment audience, a read
 *     fault) -> the queue line ALONE. No reason is claimed when none is known.
 *
 * Pure: the caller passes the mode and the resolved team label.
 */
export const UNCLAIMED_HEADLINE = 'Not yet claimed';
export const UNCLAIMED_QUEUE_FALLBACK_TEAM = 'shared';

export interface UnclaimedRoutingExplanation {
  readonly headline: string;
  /** The one-line WHY, or null when no lawful reason is known. */
  readonly why: string | null;
  readonly mode: LeadAssignmentModeToken | 'unknown';
}

export function resolveUnclaimedRouting(input: {
  readonly mode: string | null | undefined;
  /** The routing TEAM label (CC-08 plane), or null when none resolved. */
  readonly teamLabel: string | null;
}): UnclaimedRoutingExplanation {
  const team =
    input.teamLabel !== null && input.teamLabel.trim() !== ''
      ? input.teamLabel.trim()
      : UNCLAIMED_QUEUE_FALLBACK_TEAM;
  const queue = `Waiting in the ${team} queue`;
  switch (input.mode) {
    case 'round_robin':
      return {
        headline: UNCLAIMED_HEADLINE,
        why: `${queue} - no active agents to auto-assign`,
        mode: 'round_robin',
      };
    case 'manager_assign':
      return {
        headline: UNCLAIMED_HEADLINE,
        why: `${queue} - a manager assigns leads here`,
        mode: 'manager_assign',
      };
    case 'shared_pool':
      return {
        headline: UNCLAIMED_HEADLINE,
        why: `${queue} - anyone on the team can claim it`,
        mode: 'shared_pool',
      };
    default:
      // TRUTHFUL-SOURCE-OR-OMIT: the queue is a fact; the reason is not known.
      return { headline: UNCLAIMED_HEADLINE, why: queue, mode: 'unknown' };
  }
}

// ---------------------------------------------------------------------
// LEAD-RECORD-POLISH-BATCH-V1 (CC-04) -- THE SUMMARY STRIP
// ---------------------------------------------------------------------

/** The KpiTile tone vocabulary this strip uses (a subset, named locally so
 *  this module stays React-free; the component maps it 1:1). */
export type LeadSummaryTone = 'ok' | 'warm' | 'breach';

export interface LeadSummaryTile {
  readonly key: 'source' | 'waiting' | 'team' | 'claimed_by';
  readonly label: string;
  readonly value: string;
  readonly sub: string | null;
  readonly tone: LeadSummaryTone;
  readonly testId: string;
  /** Team tile only -- the finding-4 plane (pinned; never a label). */
  readonly plane?: 'open_action' | 'responsible_team';
  /** The tile whose VALUE carries a legacy pinned testid, if any. */
  readonly valueTestId?: string;
}

export const SUMMARY_TILE_LABEL: Readonly<
  Record<LeadSummaryTile['key'], string>
> = {
  /* The founder's "source badge" renders under the E5a NORMALIZED-SOURCE
     label -- 'Channel'. The crm-shell pin forbids a second 'Source' heading
     on this record ('Source' is reserved for the details-zone section title),
     and repo law outranks the shorthand in the package order. The tile is the
     source badge's ONE always-visible home; the raw attribution PARAMETERS
     collapse below it, exactly as the RECOMPOSITION LAW prescribes. */
  source: 'Channel',
  waiting: 'Waiting',
  team: TEAM_ROW_LABEL,
  claimed_by: CLAIMED_BY_ROW_LABEL,
};

export const SOURCE_NOT_CAPTURED_LABEL = 'Not captured';

/**
 * How long this lead has been waiting, from the record's OWN capture stamp
 * (`createdAt` -- the same field the header prints as "Captured"). Whole
 * minutes; null when the stamp is absent or unparseable, so the tile can say
 * so instead of showing a fabricated zero.
 *
 * NOT SLA MATH (the Attack-First rule "SLA math from the ONE classifier"):
 * this is elapsed time since a timestamp the record already displays. Every
 * SLA VERDICT on this page -- breached or clear, the waiting age the engine
 * measured, and the SLA WINDOW -- comes from the V4-P0 classifier through
 * `truth.currentSlaState` and is rendered as this tile's SUB line. The two
 * are deliberately separated so a reader can never mistake the elapsed clock
 * for an SLA judgement.
 */
export function waitingSinceMinutes(
  createdAtIso: string | null | undefined,
  nowMs: number,
): number | null {
  if (typeof createdAtIso !== 'string' || createdAtIso.trim() === '') return null;
  const t = Date.parse(createdAtIso.trim());
  if (!Number.isFinite(t) || !Number.isFinite(nowMs)) return null;
  return Math.max(0, Math.floor((nowMs - t) / 60_000));
}

/**
 * THE first-screen summary strip (CC-04): source | waiting/SLA | team |
 * claimed-by. Four FACTS, no teaching (DENSITY rules 2/3): value hero, label
 * micro, sub names the state in the user's words. An unproven figure renders
 * '-' with the truth named in `sub`, never a fake zero (DENSITY rule 6).
 *
 * SAY IT ONCE: this strip becomes the ONE home for the SLA state and the two
 * ownership planes, so the caller drops the band's SLA chip and the header's
 * old Owner / next-action-owner rows (their legacy test ids ride the tiles).
 */
export function buildLeadSummaryTiles(input: {
  readonly record: Lead360Input;
  readonly view: Lead360View;
  readonly truth: LeadRecordCurrentTruth;
  readonly ownershipPlaneRow: OwnershipPlaneRow | null;
  /** `me.leadAssignmentMode` -- server truth; absent for a caller outside
   *  the assignment audience (then no reason is claimed). */
  readonly assignmentMode: string | null | undefined;
  readonly nowMs: number;
}): readonly LeadSummaryTile[] {
  const { record, view, truth, ownershipPlaneRow, assignmentMode, nowMs } = input;
  const tiles: LeadSummaryTile[] = [];

  // 1. SOURCE -- the attribution BADGE stays visible even though the raw
  //    parameters collapse (the COLLAPSIBLE DISCIPLINE). Source-tier gated by
  //    the SAME section manifest the server projected; never widened.
  if (isLead360SectionVisible(view.visibleSections, 'source')) {
    // The tile carries THE header's existing compact source summary verbatim
    // ("Google paid search") -- the fact MOVED, its wording did not change,
    // and `hasResolvedSourceSummary` stays the one source-coherence oracle.
    const medium = sourceMediumLabel(record.utmMedium);
    const campaign =
      typeof record.utmCampaign === 'string' && record.utmCampaign.trim() !== ''
        ? record.utmCampaign.trim()
        : null;
    tiles.push({
      key: 'source',
      label: SUMMARY_TILE_LABEL.source,
      value: compactSourceSummary(record),
      sub: campaign ?? (medium !== '' ? medium : null),
      tone: 'ok',
      testId: 'lead-summary-tile-source',
    });
  }

  // 2. WAITING -- "how long has this been waiting", with the ONE classifier's
  //    verdict underneath ("Overdue by 1h 45m" / "SLA 30m" / "No SLA breach" /
  //    the honest unavailable state).
  //
  //    ONE CLOCK, NEVER TWO: when the V4-P0 classifier measured a waiting age
  //    (i.e. this record is breaching), THAT age is the value -- the engine
  //    anchors each class to its own moment (capture / qualification / last
  //    activity), so an independently measured elapsed clock could contradict
  //    the verdict right beside it ("Waiting 14d / Overdue by 1h 45m"). Only
  //    when the classifier measured nothing does the tile fall back to the
  //    record's own elapsed-since-capture -- a plain restatement of the
  //    "Captured" stamp, never an SLA judgement.
  const sla = truth.currentSlaState;
  const waited = sla.waitingMinutes ?? waitingSinceMinutes(record.createdAt, nowMs);
  tiles.push({
    key: 'waiting',
    label: SUMMARY_TILE_LABEL.waiting,
    value: waited !== null ? formatLeakageAge(waited) : '-',
    sub:
      waited === null
        ? 'Capture time not recorded'
        : (sla.overdueLabel ?? sla.slaWindowLabel ?? sla.label),
    tone: sla.kind === 'breach' ? (sla.severity === 'red' ? 'breach' : 'warm') : 'ok',
    testId: 'lead-summary-tile-waiting',
  });

  // 3. TEAM -- the ROUTING target (CC-08), role-gated by the SAME
  //    nextActionOwner manifest, resolved through the finding-4 plane rule
  //    (a personally assigned lead renders NO ladder guess) and the CC-12
  //    display-label layer (never a raw token).
  if (
    isLead360SectionVisible(view.visibleSections, 'nextActionOwner') &&
    ownershipPlaneRow !== null
  ) {
    tiles.push({
      key: 'team',
      label: SUMMARY_TILE_LABEL.team,
      value: ownershipPlaneRow.value,
      sub: ownershipPlaneRow.note,
      tone: 'ok',
      testId: 'lead-summary-tile-team',
      plane: ownershipPlaneRow.plane,
      valueTestId: 'record360-next-action-owner',
    });
  }

  // 4. CLAIMED BY -- the accountable PERSON (CC-08). Absent ownerAssignment
  //    renders NOTHING (the byte-honest flag-off posture is preserved).
  const owner = truth.currentOwner;
  if (owner.kind !== 'unknown') {
    if (owner.kind === 'assigned') {
      tiles.push({
        key: 'claimed_by',
        label: SUMMARY_TILE_LABEL.claimed_by,
        value: `${owner.label ?? 'Assigned'}${owner.isMine ? ' (you)' : ''}`,
        sub: null,
        tone: 'ok',
        testId: 'lead-summary-tile-claimed-by',
        valueTestId: 'record360-owner',
      });
    } else {
      // CC-11: the unclaimed seat EXPLAINS itself from real routing state.
      const routing = resolveUnclaimedRouting({
        mode: assignmentMode,
        teamLabel: ownershipPlaneRow?.value ?? null,
      });
      tiles.push({
        key: 'claimed_by',
        label: SUMMARY_TILE_LABEL.claimed_by,
        value: routing.headline,
        sub: owner.escalated
          ? `${routing.why ?? ''}${routing.why !== null ? ' - ' : ''}${OWNER_ESCALATED_HINT}`
          : routing.why,
        tone: owner.escalated ? 'warm' : 'ok',
        testId: 'lead-summary-tile-claimed-by',
        valueTestId: 'record360-owner',
      });
    }
  }

  return tiles;
}

// ---------------------------------------------------------------------
// OMNICHANNEL-TIMELINE-AND-LEAD-360-V1 -- THE EVENT ENVELOPE ADAPTER
// (Lead360Event wire -> the shared ui/Timeline TimelineEntry contract).
//
// Channel derivation is EVIDENCE-GATED (the truth law): a channel renders
// ONLY where a stored fact supports it --
//   - a disposition row's stored [channel:] token / picklist method
//     (phone -> call, whatsapp -> whatsapp, in_person -> walk_in);
//   - a linked telephony call event -> call;
//   - a walk-in registration -> walk_in;
//   - the capture ORIGIN event, from the lead's stored source token ONLY
//     where that token IS a channel fact (whatsapp -> whatsapp, webchat ->
//     chat, walk_in -> walk_in). Every OTHER source (paid_search, campaign,
//     offline...) is a MARKETING source, not a channel fact -- the origin
//     renders channel 'system' and the source stays in the header/details
//     (never guessed; an Office24x7-ingested lead carries marketing sources
//     indistinguishable from web-form ones, so claiming 'web' would lie).
// Everything else is 'system'. Test-pinned per source in
// lead-timeline-envelope.test.tsx.
// ---------------------------------------------------------------------

import type {
  TimelineChannel,
  TimelineEntry,
  TimelineTypeFamily,
} from '@/dashboard/ui/Timeline';
// THE compact relative-age owner (the workboard vocabulary: 'just now' /
// '5m' / '2h' / '3d') -- reused, never re-implemented, so ages read
// identically on the work queue and the record timeline.
import { formatRelativeAge } from '@/dashboard/workboard/workboard-view';

/** Origin-source tokens that ARE channel facts (everything else: system). */
const ORIGIN_SOURCE_CHANNEL: Readonly<Record<string, TimelineChannel>> = {
  whatsapp: 'whatsapp',
  webchat: 'chat',
  walk_in: 'walk_in',
};

/** Disposition contact-channel tokens that ARE channel facts. */
const DISPOSITION_CHANNEL: Readonly<Record<string, TimelineChannel>> = {
  phone: 'call',
  whatsapp: 'whatsapp',
  in_person: 'walk_in',
};

export const TIMELINE_CHANNEL_LABEL: Readonly<Record<TimelineChannel, string>> = {
  call: 'Call',
  whatsapp: 'WhatsApp',
  chat: 'Chat',
  web: 'Web',
  walk_in: 'Walk-in',
  system: 'System',
};

const CALL_PROVENANCE_PREFIX = 'calls_read_model_';

/** Pure channel derivation for one event (exported for the envelope pins). */
export function timelineChannelForEvent(
  event: Lead360Event,
  leadSourceNormalized: string | null,
): TimelineChannel {
  if (
    typeof event.dispositionChannel === 'string' &&
    DISPOSITION_CHANNEL[event.dispositionChannel] !== undefined
  ) {
    return DISPOSITION_CHANNEL[event.dispositionChannel]!;
  }
  if (
    event.eventType === 'call_logged' ||
    event.provenance.startsWith(CALL_PROVENANCE_PREFIX)
  ) {
    return 'call';
  }
  if (event.eventType === 'walkin_registered') return 'walk_in';
  if (event.eventType === 'lead_created') {
    if (
      leadSourceNormalized !== null &&
      ORIGIN_SOURCE_CHANNEL[leadSourceNormalized] !== undefined
    ) {
      return ORIGIN_SOURCE_CHANNEL[leadSourceNormalized]!;
    }
    return 'system';
  }
  return 'system';
}

/** Type family: the rail's proven icon families + 'note'. */
export function timelineTypeFamilyForEvent(event: Lead360Event): TimelineTypeFamily {
  if (event.eventType === 'note_added') return 'note';
  if (
    CALL_TIMELINE_EVENT_TYPES.has(event.eventType) ||
    (typeof event.dispositionOutcomeId === 'string' &&
      event.dispositionOutcomeId.length > 0)
  ) {
    return 'call';
  }
  if (
    event.eventType.startsWith('appointment') ||
    event.eventType === 'visited' ||
    event.eventType.startsWith('walkin') ||
    event.eventType === 'patient_arrived' ||
    event.eventType === 'patient_checked_in' ||
    event.eventType === 'consult_started'
  ) {
    return 'appointment';
  }
  if (event.eventType === 'contact_revealed') return 'reveal';
  if (event.eventType === 'lead_created' || event.eventType.startsWith('lead_')) {
    return 'person';
  }
  return 'system';
}

/** The rail's call-family tokens (kept in lockstep with railIconFamily's
 *  CALL set -- one vocabulary, re-stated once here for the pure adapter). */
const CALL_TIMELINE_EVENT_TYPES: ReadonlySet<string> = new Set([
  'called',
  'not_answered',
  'missed_call',
  'call_logged',
  'follow_up_needed',
]);

/**
 * LEAD360-G6-AUTHOR-COLUMN-FIX G6-1: the backend's own event summaries
 * (e.g. "Called via phone, by Sandbox Agent", "Note by coc", "by System")
 * carry a trailing actor clause -- the SAME "author inside detail prose"
 * defect class M2 only fixed for notes. SINGLE-POINT fix: this ONE
 * function is the only place that parses it, called once per event by
 * `buildLeadTimelineEntries` below -- never per-callsite, never mutating
 * the stored `summary` string itself (this returns a NEW derived pair; the
 * event object is untouched). `\bby\b` requires a word boundary so a
 * summary like "...nearby..." never false-matches mid-word.
 */
const ACTOR_SUFFIX_PATTERN = /,?\s*\bby\b\s+(.+)$/;

function splitActorSuffix(summary: string): { cleaned: string; actorLabel: string } {
  const match = ACTOR_SUFFIX_PATTERN.exec(summary);
  if (match === null) {
    return { cleaned: summary, actorLabel: 'System' };
  }
  const rawActor = match[1]!.trim();
  const cleaned = summary.slice(0, match.index).trim();
  // roleLabel humanizes a KNOWN role token (coc -> COC, call_center_agent ->
  // Call center, matching the shell chrome's own vocabulary); an
  // already-human name like "Sandbox Agent" is not a role-label key, so it
  // passes through roleLabel's own de-case fallback unchanged (no
  // underscores to split, already capitalized) -- ONE function, no branch
  // needed here to tell the two apart.
  return { cleaned, actorLabel: rawActor === '' ? 'System' : roleLabel(rawActor) };
}

/**
 * Build the shared-timeline envelope entries from the WINDOWED timeline
 * wire (entries arrive NEWEST-FIRST; order preserved -- the wire owns
 * determinism). `nowMs` feeds the relative age label (injected clock --
 * deterministic under test); a null/absent clock omits relative labels
 * rather than fabricating them.
 */
export function buildLeadTimelineEntries(
  events: readonly Lead360Event[],
  leadSourceNormalized: string | null,
  nowMs: number | null,
): TimelineEntry[] {
  return events.map((e, i) => {
    const reason =
      e.eventType === 'lead_not_converted' &&
      typeof e.outcomeReason === 'string' &&
      e.outcomeReason.trim() !== ''
        ? ` (${outcomeReasonLabel(e.outcomeReason)})`
        : '';
    const whenLabel = formatTimestampIst(e.occurredAt);
    const { dayLabel, timeLabel } = splitIstStamp(whenLabel);
    const channel = timelineChannelForEvent(e, leadSourceNormalized);
    const occurredMs = Date.parse(e.occurredAt);
    const isNote = e.eventType === 'note_added';
    // LEAD-360-CONFORMANCE-B2 G1: the retired Lead History table's ONE fact
    // the timeline did not already carry (the reschedule original/new
    // appointment-time pair -- the SAME two fields, SAME formatter
    // buildLeadHistoryRows used) folds into THIS entry's own detail
    // disclosure -- the timeline stays the single, comprehensive story of
    // every touch, never a second table.
    const hasReschedulePair =
      e.eventType === 'appointment_rescheduled' &&
      typeof e.previousAppointmentTime === 'string' &&
      e.previousAppointmentTime.trim() !== '' &&
      typeof e.newAppointmentTime === 'string' &&
      e.newAppointmentTime.trim() !== '';
    // LEAD360-G6-AUTHOR-COLUMN-FIX G6-1: split the actor OUT of the summary
    // prose HERE, once, before it becomes `actorLine` -- the author column
    // renders `authorLabel`, never `evidenceLabel` (the provenance CLASS)
    // and never a summary string with the actor still concatenated inside.
    const { cleaned: summaryWithoutActor, actorLabel } =
      typeof e.summary === 'string' && e.summary.trim() !== ''
        ? splitActorSuffix(e.summary)
        : { cleaned: '', actorLabel: 'System' };
    return {
      key: `${e.eventType}:${e.occurredAt}:${i}`,
      occurredAtIso: e.occurredAt,
      dayLabel,
      timeLabel,
      whenLabel,
      relativeLabel:
        nowMs !== null && Number.isFinite(occurredMs)
          ? formatRelativeAge(e.occurredAt, nowMs)
          : null,
      channel,
      channelLabel: TIMELINE_CHANNEL_LABEL[channel],
      typeFamily: timelineTypeFamilyForEvent(e),
      eventType: e.eventType,
      label: `${eventTypeLabel(e.eventType)}${reason}`,
      actorLine: summaryWithoutActor !== '' ? summaryWithoutActor : null,
      authorLabel: actorLabel,
      evidenceLabel:
        EVIDENCE_CLASS_LABEL[
          isNote ? 'agent_recorded' : eventEvidenceClass(e)
        ],
      voided: e.noteVoided === true,
      // Lawful detail payloads ONLY: the note text (this wire is org-fenced +
      // role-gated), the reschedule before/after pair, or a disposition's
      // callback-due fact. Everything else has no stored detail -> no
      // disclosure renders (A4).
      detail:
        isNote && typeof e.noteText === 'string' && e.noteText.trim() !== ''
          ? e.noteText
          : hasReschedulePair
            ? `${formatTimestampIst(e.previousAppointmentTime as string)} -> ${formatTimestampIst(e.newAppointmentTime as string)}`
            : typeof e.dispositionCallbackAtIso === 'string' &&
                e.dispositionCallbackAtIso.trim() !== ''
              ? `Callback due ${formatTimestampIst(e.dispositionCallbackAtIso)}`
              : null,
      detailSummaryLabel: isNote
        ? 'View note'
        : hasReschedulePair
          ? 'Reschedule detail'
          : typeof e.dispositionCallbackAtIso === 'string' &&
              e.dispositionCallbackAtIso.trim() !== ''
            ? 'Callback'
            : null,
    };
  });
}
